import { LoaderFunctionArgs, useLoaderData } from "react-router"
import { DocsRouterLink as RouterLink } from "~/docs-data/docs-link"
import {
  ArrowRightIcon,
  ChartLineIcon,
  ClockIcon,
  DatabaseIcon,
  MapIcon,
  MapPinIcon,
  RadioTowerIcon,
  SplitIcon,
  VectorSquareIcon,
  type LucideIcon,
} from "lucide-react"
import { H1, P, Section } from "~/components/Typography"
import { MarkdownBody } from "~/components/MarkdownBody"
import { LocationTypeBadge } from "~/components/LocationTypeBadge"
import { CustomBoundariesIcon } from "~/components/CustomBoundariesIcon"
import { CountryShape } from "~/docs-data/country-catalogs"
import { getCountryCatalog, datasetsForCountry } from "~/docs-data/helpers"
import { locationSets, supplySources } from "~/docs-data/concepts"
import { getDocsCatalogForRequest } from "~/server/docs-catalog-store.server"
import type { DatasetEntry } from "~/docs-data/datasets"

const LOCATION_TYPES: { id: "place" | "area" | "way"; label: string; icon: LucideIcon; blurb: string }[] = [
  {
    id: "place",
    label: "Place",
    icon: MapPinIcon,
    blurb: "Activity at points of interest: stores, venues, attractions.",
  },
  {
    id: "area",
    label: "Area",
    icon: VectorSquareIcon,
    blurb: "Activity within regions, neighborhoods, or grid cells.",
  },
  {
    id: "way",
    label: "Way",
    icon: SplitIcon,
    blurb: "Movement along roads and pedestrian routes.",
  },
]

export const loader = async ({ request, params }: LoaderFunctionArgs) => {
  const { countries, datasets } = await getDocsCatalogForRequest(request)
  const cc = getCountryCatalog(countries, params.country ?? "")
  if (!cc) throw new Response("Not found", { status: 404 })
  return {
    cc,
    countryDatasets: datasetsForCountry(countries, datasets, cc.code),
  }
}

export default function CountryOverview() {
  const { cc, countryDatasets } = useLoaderData<typeof loader>()

  // Group datasets by location type → map, preserving discovery order.
  const byType: Record<"place" | "area" | "way", Map<string, DatasetEntry[]>> = {
    place: new Map(),
    area: new Map(),
    way: new Map(),
  }
  for (const d of countryDatasets) {
    const group = byType[d.locationType]
    if (!group.has(d.locationSet)) group.set(d.locationSet, [])
    group.get(d.locationSet)!.push(d)
  }

  const presentTypes = LOCATION_TYPES.filter(({ id }) => byType[id].size > 0)
  const countrySupplies = supplySources.filter((s) => s.country === cc.countryCode)
  const totalMaps = presentTypes.reduce((n, { id }) => n + byType[id].size, 0)

  return (
    <div>
      {/* Hero ------------------------------------------------------------ */}
      <Section>
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="flex-1 min-w-[320px]">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-4xl" aria-hidden>
                {cc.flag}
              </span>
              <H1 className="!mb-0">{cc.name}</H1>
            </div>
            {cc.description ? (
              <MarkdownBody source={cc.description} />
            ) : (
              <P className="text-muted-foreground">
                Foot-traffic and movement data for {cc.name}.
              </P>
            )}
          </div>
          <div
            aria-hidden
            className="hidden sm:block w-32 h-32 shrink-0 text-muted-foreground/30"
          >
            <CountryShape catalog={cc} className="w-full h-full" />
          </div>
        </div>
      </Section>

      {/* Stats strip ----------------------------------------------------- */}
      <Section>
        <div className="grid grid-cols-2 gap-3">
          <Stat icon={DatabaseIcon} label="Datasets" value={countryDatasets.length} />
          <Stat icon={MapIcon} label="Maps" value={totalMaps} />
        </div>
      </Section>

      {/* By location type ----------------------------------------------- */}
      {presentTypes.length > 0 && (
        <Section>
          <h2 className="text-sm uppercase tracking-wide text-muted-foreground font-semibold mb-3">
            What's available
          </h2>
          <div className="space-y-6">
            {presentTypes.map(({ id, label, icon: Icon, blurb }) => {
              const mapsByID = byType[id]
              return (
                <div
                  key={id}
                  className="rounded-lg border bg-card overflow-hidden"
                >
                  <div className="flex items-center justify-between gap-3 px-5 py-3 border-b bg-muted/30">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary">
                        <Icon className="h-4 w-4" />
                      </span>
                      <div>
                        <div className="flex items-center gap-2">
                          <RouterLink
                            to={`/docs/datasets/${cc.code}/location-types/${id}`}
                            className="font-semibold text-sm text-foreground hover:text-primary transition-colors"
                          >
                            {label}
                          </RouterLink>
                          <LocationTypeBadge type={id} iconOnly />
                        </div>
                        <p className="text-xs text-muted-foreground">{blurb}</p>
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {mapsByID.size} map{mapsByID.size === 1 ? "" : "s"}
                    </span>
                  </div>
                  <ul className="divide-y">
                    {[...mapsByID.entries()].map(([mapId, ds]) => {
                      const editorial = locationSets.find((ls) => ls.id === mapId)
                      const mapName =
                        ds[0]?.locationSetName ?? editorial?.shortName ?? mapId
                      return (
                        <li key={mapId} className="px-5 py-3">
                          <div className="flex items-baseline justify-between gap-3 mb-1">
                            <RouterLink
                              to={`/docs/datasets/${cc.code}/location-sets/${mapId}`}
                              className="text-sm font-medium text-foreground hover:text-primary transition-colors inline-flex items-center gap-1.5"
                            >
                              <MapIcon className="w-3.5 h-3.5 text-muted-foreground" />
                              {mapName}
                            </RouterLink>
                            <span className="text-xs text-muted-foreground">
                              {ds.length} dataset{ds.length === 1 ? "" : "s"}
                            </span>
                          </div>
                          <ul className="flex flex-wrap gap-1.5 mt-1.5">
                            {ds.map((d) => (
                              <li key={d.slug}>
                                <RouterLink
                                  to={`/docs/datasets/${cc.code}/${d.slug}`}
                                  className="inline-flex items-center gap-1 rounded-full border bg-background px-2 py-0.5 text-xs text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                                >
                                  <DatabaseIcon className="w-3 h-3" />
                                  {d.name}
                                  <span className="text-[10px] uppercase tracking-wide opacity-60">
                                    {d.cadence}
                                  </span>
                                </RouterLink>
                              </li>
                            ))}
                          </ul>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              )
            })}
          </div>
          {/* Custom boundaries — pre-built maps are not the only geography we
              serve; this comes up in most sales conversations, so say it. */}
          <div className="mt-4 rounded-lg border border-dashed bg-card p-4 flex items-start gap-3">
            <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
              <CustomBoundariesIcon className="h-4.5 w-4.5" />
            </span>
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">
                Custom boundaries.
              </span>{" "}
              Have your own map? The {cc.name} metrics can also be attributed
              to boundaries you define (trade zones, sales territories,
              property outlines), so the data lines up with the geography your
              organization already uses. Reach out to{" "}
              <a
                href="mailto:support@pine59.com"
                className="text-primary hover:underline"
              >
                support@pine59.com
              </a>{" "}
              to set it up.
            </p>
          </div>
        </Section>
      )}

      {/* How it all fits together --------------------------------------- */}
      <Section>
        <h2 className="text-sm uppercase tracking-wide text-muted-foreground font-semibold mb-3">
          How it all fits together
        </h2>
        <div className="rounded-lg border bg-card p-5">
          <p className="text-sm leading-relaxed text-muted-foreground mb-4">
            Everything in {cc.name} starts from <strong>signal</strong>, the
            underlying mobile signal we observe. Signal is what tells us a device
            was present somewhere; every dataset below is a product built on top
            of it.
          </p>
          <div className="flex flex-wrap items-stretch gap-2">
            <Block
              icon={RadioTowerIcon}
              title="Signal"
              desc="The raw signal: where devices are, when."
            />
            <FlowArrow label="feeds" />
            <Block
              icon={ChartLineIcon}
              title="Metric"
              desc="What we measure: foot traffic, trade area, demographics, …"
            />
            <FlowSep />
            <Block
              icon={MapIcon}
              title="Map"
              desc="Where we measure it: a set of venues, neighborhoods, or routes."
            />
            <FlowSep />
            <Block
              icon={ClockIcon}
              title="Cadence"
              desc="How often: daily, weekly, monthly, or quarterly."
            />
            <FlowArrow label="=" />
            <Block
              icon={DatabaseIcon}
              title="Dataset"
              desc="One queryable surface, e.g. monthly foot traffic at Safegraph Places."
              highlight
            />
          </div>
          <p className="text-sm leading-relaxed text-muted-foreground mt-4">
            Each row in the list below is one specific{" "}
            <strong>metric × map × cadence</strong> combination, materialized
            from the signal listed at the bottom of this page.
          </p>
        </div>
      </Section>

      {/* Signal ---------------------------------------------------------- */}
      {countrySupplies.length > 0 && (
        <Section>
          <h2 className="text-sm uppercase tracking-wide text-muted-foreground font-semibold mb-3">
            Signal
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {countrySupplies.map((s) => (
              <RouterLink
                key={s.id}
                to={`/docs/datasets/${cc.code}/supply/${s.id}`}
                className="group flex items-start gap-3 rounded-lg border bg-card hover:border-primary hover:shadow-sm transition-all p-4"
              >
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary shrink-0">
                  <RadioTowerIcon className="h-4 w-4" />
                </span>
                <div className="flex-1">
                  <div className="font-semibold text-sm text-foreground group-hover:text-primary transition-colors">
                    {s.shortName}
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                    {s.description}
                  </p>
                </div>
                <ArrowRightIcon className="w-3.5 h-3.5 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary mt-1" />
              </RouterLink>
            ))}
          </div>
        </Section>
      )}

    </div>
  )
}

function Stat({
  icon: Icon,
  label,
  value,
}: {
  icon: LucideIcon
  label: string
  value: number
}) {
  return (
    <div className="rounded-lg border bg-card p-3 flex items-center gap-3">
      <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary shrink-0">
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0">
        <div className="text-lg font-semibold text-foreground leading-tight">
          {value}
        </div>
        <div className="text-xs text-muted-foreground truncate">{label}</div>
      </div>
    </div>
  )
}

/** One building block in the "How it all fits together" flow. */
function Block({
  icon: Icon,
  title,
  desc,
  highlight,
}: {
  icon: LucideIcon
  title: string
  desc: string
  highlight?: boolean
}) {
  return (
    <div
      className={
        "flex-1 min-w-[140px] basis-[170px] rounded-md border p-3 " +
        (highlight ? "border-primary/40 bg-primary/5" : "bg-background")
      }
    >
      <div className="flex items-center gap-2 mb-1">
        <span className="inline-flex h-6 w-6 items-center justify-center rounded bg-primary/10 text-primary shrink-0">
          <Icon className="h-3.5 w-3.5" />
        </span>
        <span className="text-sm font-semibold text-foreground">{title}</span>
      </div>
      <p className="text-xs leading-snug text-muted-foreground">{desc}</p>
    </div>
  )
}

/** "feeds" / "=" connector with a horizontal rule above and below the label. */
function FlowArrow({ label }: { label: string }) {
  return (
    <div
      aria-hidden
      className="flex items-center justify-center text-xs font-semibold uppercase tracking-wide text-muted-foreground/70 shrink-0 px-1"
    >
      {label}
    </div>
  )
}

/** "×" separator between the metric / map / cadence ingredients. */
function FlowSep() {
  return (
    <div
      aria-hidden
      className="flex items-center justify-center text-lg text-muted-foreground/50 shrink-0 px-0.5"
    >
      ×
    </div>
  )
}
